import DisplayGate from '../../lib/display_gate';
import Events, { EventTypes } from '../../lib/events';
import Logger from '../../lib/logger';
import {
  DisplayGateKeys,
  FIRST_SLOT_GATE_KEYS,
  DEFAULT_FIRST_SLOT_RESPONSE_TIMEOUT,
} from '../../lib/display_gate_keys';

// A backstop must be a positive number of ms. DisplayGate reads 0 as "no backstop", so letting a
// configured 0 (or a negative / non-numeric value) through would turn a "don't wait long" setting into
// an unbounded wait; fall back to the default instead.
const positiveOr = (value, fallback) => (typeof value === 'number' && value > 0 ? value : fallback);

// Whether a designated first slot can still request and answer. One that was destroyed, or that cannot
// be displayed (ads disabled, wrong device, no sizes, ...), never will — so nothing should wait on its
// response. Such a slot stays registered and designated (nothing un-designates it), which is why the
// gate's owners ask this rather than only "is a first slot designated".
const canStillAnswer = slot => !slot.isDestroyed() && slot.canBeDisplayed();

// Any gate key other than Concert's own two first-slot gates belongs to a page or an external system.
const isExternalGateKey = key => !FIRST_SLOT_GATE_KEYS.includes(key);

// The firstSlotResponse gate's backstop (ms): `settings.firstSlotResponseTimeout`, or its default when
// that is unset or not a positive number. Every countdown of the gate runs for this long from its last
// (re)start, and it is the `timeout` the gate is armed with.
const getFirstSlotResponseTimeout = app =>
  positiveOr(app.settings.firstSlotResponseTimeout, DEFAULT_FIRST_SLOT_RESPONSE_TIMEOUT);

// Asked when the firstSlotResponse backstop expires, before the gate opens: should the waiting slots keep
// waiting? Yes while the designated first slot is still on its way — it can still answer, is not parked
// behind display(), and has not requested yet (e.g. its bid auction is running long). The countdown then
// renews for another `firstSlotResponseTimeout`, so waiting slots are not released ahead of a first slot
// that is about to request. The check has to live here: once the gate opens its waiters are already
// released, and a first slot that requests later cannot take that back.
//
// Granted at most ONCE per gate. The auction that delays the first slot's request is itself bounded by
// Prebid's `auctionTimeout`, so a single extra window covers a slow one. A first slot that has still not
// requested after that is stuck — a bid that never returns — and the waiting slots must not be held for
// it, so the gate opens and they render. That makes the pre-request wait two windows at most.
//
// Answers 0 once the first slot HAS requested, so `firstSlotResponseTimeout` stays a real backstop
// against a GAM response that never comes. A page with no designated first slot gets no renewal either:
// nothing is known to be coming.
function extendFirstSlotResponseOnTimeout(app, extensionsGranted) {
  if (extensionsGranted > 0) return 0;

  const firstSlot = app.slots.all().find(slot => slot.isFirstSlot());
  if (!firstSlot || firstSlot.isDisplayed() || firstSlot.isDeferDisplay() || !canStillAnswer(firstSlot)) return 0;

  return getFirstSlotResponseTimeout(app);
}

/**
 * Public API for the display-gate registry: a generic set of one-shot gates (`app.displayGates`,
 * keyed by category) that hold back ad rendering until each category has displayed. A slot's
 * render() awaits every active gate it is not exempt from (see {@link ConcertAds#gatesBlocking} and
 * a slot's `exemptFromBlocking` list).
 *
 * The registry is generic — a page or external system can arm any gate key with
 * `blockUntilDisplay(key)` and open it with `releaseDisplay(key)`. Two concrete gates ship built-in
 * (keys in {@link DisplayGateKeys}) when `settings.waitForFirstSlot`. Exactly ONE slot (config
 * `firstSlot: true`) is designated the first slot; it is exempt from both:
 *   - 'firstSlot' — armed at install; opens when the first slot fires its request, releasing the rest
 *     of the page.
 *   - 'firstSlotResponse' — opt-in (slot config `waitForFirstSlotResponse: true`, defaulted on for
 *     out-of-page and adhesion slots); opens when the first slot's GAM response renders, or on its
 *     backstop. Not armed at install: it is armed when a waiting slot registers, and armed again by
 *     any later waiting slot that registers while the first slot has still not answered (see
 *     {@link ConcertAds#armFirstSlotResponseGate}). Its backstop is `settings.firstSlotResponseTimeout`,
 *     counted from the gate's last (re)start: arming, finalizeSlots(), the end of an external hold, and
 *     the first slot's request — which re-anchors it, so that setting budgets only GAM's answer and not
 *     the bid auction before it. If it expires while the first slot is still on its way (can still
 *     answer, not parked, not yet requested) the countdown renews instead of opening — ONCE — so a slow
 *     bid auction does not release the waiting slots ahead of it, while a first slot stuck on a bid that
 *     never returns cannot hold them past two windows.
 *
 * See {@link DisplayGate} for the underlying one-shot gate primitive.
 */
export function displayControlMixin(ConcertAds) {
  /**
   * Arm a display gate for `key`: block every non-exempt slot's render() until the gate is released
   * (via {@link ConcertAds#releaseDisplay}) or its backstop fires. Idempotent — a second call for an
   * already-armed key is a no-op, so an external system can call it defensively.
   *
   * @param {string}   key                Category to gate.
   * @param {Object}   [options]
   * @param {Function} [options.onOpen]   Callback run synchronously the instant the gate opens.
   * @param {number}   [options.timeout]  Backstop (ms) that opens the gate if it is never released.
   *   Defaults to 0 (disabled): a manual block holds until an explicit releaseDisplay. Pass a
   *   positive value to arm a backstop.
   * @param {boolean}  [options.deferTimeout]  Keep `timeout` but hold off starting its countdown until
   *   the owner starts it (see {@link DisplayGate#startTimeout}). Until then the gate has no backstop.
   *   Internal: used by the built-in `firstSlotResponse` gate; not part of the documented page API.
   * @param {Function} [options.extendOnTimeout]  Asked when the backstop expires, before the gate opens:
   *   return a positive number of ms to keep the gate pending and count down again, anything else to let
   *   it open (see {@link DisplayGate}). Internal: used by the built-in `firstSlotResponse` gate; not part
   *   of the documented page API.
   *
   * Arming any key other than the two built-in ones is an EXTERNAL hold on the page (see
   * {@link ConcertAds#hasExternalDisplayHold}): the built-in first-slot backstops are paused for as long
   * as it is pending, and restart with a full window when the last external gate opens — so a backstop
   * cannot run down behind the hold and release waiting slots ahead of the first slot.
   */
  ConcertAds.prototype.blockUntilDisplay = function(
    key,
    { onOpen, timeout = 0, deferTimeout = false, extendOnTimeout } = {}
  ) {
    if (!key || this.displayGates.has(key)) return;
    const gate = new DisplayGate(key, { timeout, onOpen, deferTimeout, extendOnTimeout });
    this.displayGates.set(key, gate);
    Logger.log(`[DisplayGate] '${key}' armed`);

    if (isExternalGateKey(key)) {
      // The new gate is not among those finalizeSlots() declared done, so it counts as a hold — even
      // when armed after a finalizeSlots() — until the next finalizeSlots() or its own release.
      if (this.hasExternalDisplayHold()) this.pauseFirstSlotTimers();
      gate.runOnOpen(() => this.resumeFirstSlotTimers());
    }
  };

  /**
   * Whether an external gate is pending: any armed gate that is not one of Concert's own first-slot
   * gates ({@link FIRST_SLOT_GATE_KEYS}) and has not opened — e.g. the hold an interstitial system puts
   * on the page with `blockUntilDisplay('interruptive')`. A gate that opened on its own backstop stays
   * in the registry, so this reads `opened` rather than mere presence.
   *
   * An external gate that {@link ConcertAds#finalizeSlots} has declared done does not count, even if it
   * is still pending: finalizing means the page is done with the holds that exist at that point. A gate
   * armed after that is a new hold and counts again, until the next finalizeSlots() or its own release.
   *
   * @return {boolean}
   */
  ConcertAds.prototype.hasExternalDisplayHold = function() {
    return [...this.displayGates.values()].some(
      gate => isExternalGateKey(gate.key) && !gate.opened && !this.finalizedExternalGates.has(gate)
    );
  };

  /**
   * Hold the backstops of the built-in first-slot gates that are armed. Called when an external gate
   * arms, and when a built-in gate is (re)armed while one is already pending. Idempotent.
   */
  ConcertAds.prototype.pauseFirstSlotTimers = function() {
    FIRST_SLOT_GATE_KEYS.forEach(key => {
      const gate = this.displayGates.get(key);
      if (gate && gate.pauseTimeout()) {
        Logger.log(`[DisplayGate] '${key}' countdown paused (external display gate pending)`);
      }
    });
  };

  /**
   * Lift the hold on the built-in first-slot backstops once no external gate is pending, starting each
   * from scratch so the first slot gets its WHOLE window after the hold: `firstSlot` its configured
   * timeout (`firstSlotTimeout`), `firstSlotResponse` its `firstSlotResponseTimeout`. A no-op while
   * another external gate is still pending.
   */
  ConcertAds.prototype.resumeFirstSlotTimers = function() {
    if (this.hasExternalDisplayHold()) return;

    const firstSlotGate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT);
    if (firstSlotGate && firstSlotGate.resumeTimeout()) {
      Logger.log(`[DisplayGate] '${firstSlotGate.key}' countdown restarted (external display gates opened)`);
    }

    const responseGate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT_RESPONSE);
    if (responseGate && responseGate.resumeTimeout(getFirstSlotResponseTimeout(this))) {
      Logger.log(`[DisplayGate] '${responseGate.key}' countdown restarted (external display gates opened)`);
    }
  };

  /**
   * Release the `key` gate: open it (running its onOpen hooks, then resolving its promise, which
   * unblocks every slot awaiting it) and drop it from the registry. Idempotent and safe to call for
   * a gate that was never armed — a no-op then.
   *
   * @param {string} key
   * @param {string} [reason] for logging.
   */
  ConcertAds.prototype.releaseDisplay = function(key, reason = 'released') {
    const gate = this.displayGates.get(key);
    if (!gate) return;
    gate.open(reason);
    this.displayGates.delete(key);
  };

  /**
   * The set of gate promises a slot must await before it may request — every ACTIVE gate whose key
   * is not in the slot's `exemptFromBlocking` list. A gate opened via its backstop timer stays in the
   * registry, so its already-resolved promise may be returned here; awaiting it is a harmless no-op.
   *
   * @param  {Slot}      slot
   * @return {Promise[]}
   */
  ConcertAds.prototype.gatesBlocking = function(slot) {
    const exempt = (slot && slot.exemptFromBlocking) || [];
    return [...this.displayGates.values()].filter(gate => !exempt.includes(gate.key)).map(gate => gate.promise);
  };

  /**
   * Signal that every slot has been declared on the page, and emit `slotsFinalized`. Called once
   * from the page after all `registerSlot`/install calls (see pmc-adm-v2 concert-footer.php), or
   * automatically at the end of install() when `settings.autoFinalizeSlots` is set.
   *
   * Sweeps the special slots whose IntersectionObserver never fires on its own (out-of-page and
   * adhesion) via {@link renderPendingSpecialSlots}: now that every slot is declared, force-request
   * the ones that would otherwise stay registered but never fetched. Done before the gate releases
   * below so a forced OOP request is ordered ahead of the general pack that the release unblocks. A
   * special slot that waits on the `firstSlotResponse` gate is not forced here but when that gate
   * opens (see {@link renderPendingSpecialSlots}).
   *
   * Then releases the `firstSlot` gate UNCONDITIONALLY. This is deliberate — it is not a missing
   * "has the first slot requested yet?" check. "Every slot is declared" is itself the signal to stop
   * holding the pack, because the gate is not what schedules the first slot:
   *
   *   - considerFirstSlot marks the designated slot eager (isEagerEligible) AND exempt from
   *     `firstSlot`, so its request is already under way from its own inserted() — it never waited
   *     on this gate. With no bid to wait for it renders there directly; with one, BidManager#addBid
   *     auctions an eager slot immediately instead of parking it behind the lazy-bid observer, so it
   *     is in the first auction and requests from bidded(). The gate's job is to hold the OTHER
   *     slots behind that head start.
   *   - In the normal case this release is therefore already a no-op: the first slot's own render()
   *     calls releaseFirstSlotHold() the moment its request fires, which deletes the gate, and
   *     releaseDisplay short-circuits on an unarmed key.
   *   - So the unconditional release is the BACKSTOP for when that never happens — no slot was ever
   *     designated, the designated one bailed in canBeDisplayed(), or an external owner
   *     (`autoReleaseFirstSlot: false`) forgot to release. Without it those pages would hold every
   *     slot until the firstSlot backstop timer expired.
   *
   * Corollary: call finalizeSlots() LATE (after the real last registerSlot) when you want a
   * meaningful first-slot gate, rather than relying on `autoFinalizeSlots`. autoFinalizeSlots fires
   * it synchronously at the end of install(), and a prebid-eligible first slot requests from
   * bidded() — canDisplayImmediately() is false while it has a bid — so the gate opens before that
   * auction returns. Pairing the two breaks nothing; it just makes the gate a no-op. Documented in
   * instance-options.md.
   *
   * `slotsFinalized` is where pre-auction targeting providers (Optimera) apply their targeting, now
   * that the full slot list is known but before the ad requests fire. See
   * src/plugins/optimera_common.js onSlotsFinalized.
   *
   * Unlike `firstSlot`, the `firstSlotResponse` gate is NOT released unconditionally: "every slot is
   * declared" says nothing about the first slot's response, and autoFinalizeSlots fires this at the
   * end of install(), which would defeat the gate. It is released only when no first slot can still
   * answer — none is designated, or the designated one is destroyed or cannot be displayed — since
   * then no response can ever open it and the opted-in slots would just sit out the full timeout.
   *
   * Finalizing also ends the external display holds that exist at that point as far as the first-slot
   * backstops go (see `hasExternalDisplayHold`; the external gates themselves are NOT released — that
   * is their owner's `releaseDisplay`), and restarts a still-pending `firstSlotResponse` countdown from
   * scratch, so the first slot gets its whole window after the page says it is ready. An external gate
   * armed after a finalizeSlots() holds the backstops again until the next finalizeSlots() or its own
   * release.
   *
   * Idempotent — safe to call more than once: the sweep skips already-fetched slots, releaseDisplay
   * is idempotent, and the score-apply behind `slotsFinalized` is safe to re-run.
   */
  ConcertAds.prototype.finalizeSlots = function() {
    renderPendingSpecialSlots(this);

    // Response gate FIRST: the deferred special-slot requests hang off its onOpen hooks, which run
    // synchronously inside the release. Opening it before `firstSlot` queues those forced requests
    // ahead of the pack that the firstSlot release unblocks, as the sweep promises.
    if (!this.slots.all().some(slot => slot.isFirstSlot() && canStillAnswer(slot))) {
      this.releaseDisplay(DisplayGateKeys.FIRST_SLOT_RESPONSE, 'no-first-slot');
    }

    this.releaseDisplay(DisplayGateKeys.FIRST_SLOT);

    // Finalizing means the page is done with the external display holds that exist right now (see
    // hasExternalDisplayHold): stop pausing behind them, and restart the response countdown from
    // scratch — lifting a pause if there is one, or re-anchoring a running countdown — so the first
    // slot gets its whole window from the moment everything is declared. An external gate armed after
    // this is a new hold; it pauses again until the next finalizeSlots() or its own release.
    this.displayGates.forEach(gate => {
      if (isExternalGateKey(gate.key)) this.finalizedExternalGates.add(gate);
    });
    this.restartFirstSlotResponseTimer('slots finalized', { liftPause: true });

    this.hasFinalizedSlots = true;
    Events.emit(EventTypes.slotsFinalized);
  };

  /**
   * Consider a freshly-created slot for first-slot designation. Called before inserted() from both
   * install (settings.slots) and registerSlot. Designates the slot when the feature is on, the gate
   * is armed and still blocking, no slot has been designated yet (N=1 — the first eligible wins), the
   * slot config marks it (`firstSlot: true`), and the slot type participates in the display gates
   * (GAM only).
   *
   * On designation: marks the slot the first slot (so isEagerEligible() is true — an out-of-view
   * first slot still requests and never stalls the gate) and exempts it from BOTH first-slot gates
   * ({@link FIRST_SLOT_GATE_KEYS}: `firstSlot` and `firstSlotResponse`) so its own render() does not
   * deadlock waiting on a gate it is meant to open — even if it was also flagged
   * `waitForFirstSlotResponse`.
   *
   * @param  {Slot}    slot
   * @return {boolean} whether the slot was designated the first slot
   */
  ConcertAds.prototype.considerFirstSlot = function(slot) {
    if (!this.settings.waitForFirstSlot) return false;

    const gate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT);
    // Not armed / already opened: nothing to gate. isFirstSlot() off the slots is the source of
    // truth for "already designated" (N=1) — no parallel flag to keep in sync.
    if (!gate || gate.opened || this.slots.all().some(s => s.isFirstSlot())) return false;

    if (!slot || !slot.data) return false;
    if (slot.data.firstSlot !== true) return false;
    if (typeof slot.supportsDisplayGates !== 'function' || !slot.supportsDisplayGates()) return false;

    slot._state.firstSlot = true;
    FIRST_SLOT_GATE_KEYS.forEach(key => {
      if (!slot.exemptFromBlocking.includes(key)) {
        slot.exemptFromBlocking.push(key);
      }
    });

    Logger.log(`[FirstSlot] ${slot.name} designated as the first slot`);
    return true;
  };

  /**
   * The registration-time entry point for every freshly-created slot (install's settings.slots and
   * registerSlot both call it, before inserted()). Runs {@link ConcertAds#considerFirstSlot}, then —
   * for a slot that waits on the `firstSlotResponse` gate (an opted-in or auto-flagged out-of-page /
   * adhesion slot — see Slot#waitsOnFirstSlotResponse) — arms that gate via
   * {@link ConcertAds#armFirstSlotResponseGate}, so it exists before the slot's render() asks what
   * blocks it. A page with no such slot never arms it.
   *
   * @param  {Slot}    slot
   * @return {boolean} whether the slot was designated the first slot
   */
  ConcertAds.prototype.considerSlotForGates = function(slot) {
    const designated = this.considerFirstSlot(slot);

    if (
      !designated &&
      slot &&
      typeof slot.supportsDisplayGates === 'function' &&
      slot.supportsDisplayGates() &&
      typeof slot.waitsOnFirstSlotResponse === 'function' &&
      slot.waitsOnFirstSlotResponse()
    ) {
      this.armFirstSlotResponseGate('waiting-slot-registered');
    }

    return designated;
  };

  /**
   * Arm the `firstSlotResponse` gate for a slot that waits on it, and (re)start its countdown. Called
   * each time such a slot registers (see {@link ConcertAds#considerSlotForGates}) — not once at
   * install() — so a waiting slot that registers late, after the gate's backstop already fired, waits
   * again instead of requesting ahead of a first slot that still has not answered.
   *
   * Arms nothing when there is nothing left to wait on: the first slot has already rendered its
   * response, or can no longer answer (destroyed, or cannot be displayed), or no first slot is
   * designated and none can be any more (the `firstSlot` gate is no longer pending). Otherwise, by the
   * gate's current state:
   *   - absent  → a new gate is armed.
   *   - opened  → its backstop fired before the first slot answered (a timed-out gate stays in the
   *               registry until the answer releases it), so it is replaced by a fresh pending gate.
   *   - pending → kept; its countdown is restarted so this newcomer gets a full window.
   *
   * The countdown runs from now for `firstSlotResponseTimeout`. If it expires while the first slot is
   * still on its way ({@link extendFirstSlotResponseOnTimeout}) it renews instead of opening, but only
   * once — so the pre-request wait is at most two windows, and a first slot stuck on a bid that never
   * returns lets the waiting slots render. The first slot's request re-anchors the countdown
   * ({@link ConcertAds#restartFirstSlotResponseTimer}), so the timeout budgets only GAM's answer. While
   * an external gate is pending ({@link ConcertAds#hasExternalDisplayHold}) the gate is armed with its
   * countdown paused instead, and {@link ConcertAds#resumeFirstSlotTimers} starts it afterwards.
   *
   * @param {string} [reason] for logging.
   */
  ConcertAds.prototype.armFirstSlotResponseGate = function(reason = 'armed') {
    if (!this.settings.waitForFirstSlot) return;

    const firstSlot = this.slots.all().find(slot => slot.isFirstSlot());
    if (firstSlot) {
      if (firstSlot.isRendered() || !canStillAnswer(firstSlot)) return;
    } else {
      const firstSlotGate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT);
      if (!firstSlotGate || firstSlotGate.opened) return;
    }

    let gate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT_RESPONSE);
    if (gate && gate.opened) {
      this.displayGates.delete(DisplayGateKeys.FIRST_SLOT_RESPONSE);
      gate = null;
    }
    if (!gate) {
      this.blockUntilDisplay(DisplayGateKeys.FIRST_SLOT_RESPONSE, {
        timeout: getFirstSlotResponseTimeout(this),
        deferTimeout: true,
        extendOnTimeout: extensionsGranted => extendFirstSlotResponseOnTimeout(this, extensionsGranted),
      });
      gate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT_RESPONSE);
    }

    // An external gate holds the page: keep the countdown from running down behind it. It starts, with
    // a full window, when the last external gate opens (resumeFirstSlotTimers).
    if (this.hasExternalDisplayHold()) {
      gate.pauseTimeout();
      Logger.log(
        `[DisplayGate] '${gate.key}' armed with its countdown paused (${reason}; external display gate pending)`
      );
      return;
    }

    if (gate.restartTimeout(getFirstSlotResponseTimeout(this))) {
      Logger.log(`[DisplayGate] '${gate.key}' countdown (re)started (${reason})`);
    }
  };

  /**
   * Re-anchor the `firstSlotResponse` countdown to now, for `firstSlotResponseTimeout`. Called from the
   * two points where the gate should get a whole fresh window:
   *   - the designated first slot's request ({@link Slot#firstSlotRequested}), so the timeout budgets
   *     only GAM's response and not the registration-to-request gap (the bid auction). Once the first
   *     slot has requested the countdown no longer renews at expiry
   *     ({@link extendFirstSlotResponseOnTimeout}), so this is also what turns `firstSlotResponseTimeout`
   *     into a real backstop.
   *   - {@link ConcertAds#finalizeSlots}, which passes `liftPause` — so the first slot gets its whole
   *     window from the moment the page says every slot is declared.
   *
   * No-op when the gate is unarmed or already open.
   *
   * @param {string}  [reason]             for logging.
   * @param {Object}  [options]
   * @param {boolean} [options.liftPause]  Whether to start the countdown even if it is PAUSED. A gate
   *   paused by an external display hold ({@link ConcertAds#pauseFirstSlotTimers}) ignores a plain
   *   restart, which is what the first slot's request wants: an external system still owns the page, so
   *   its hold must keep holding even if the first slot got its request out. `finalizeSlots()` passes
   *   true because it means the opposite — the page has declared the holds that exist right now done, so
   *   the countdown should run from here. Default false. Either way an unpaused gate is simply restarted.
   */
  ConcertAds.prototype.restartFirstSlotResponseTimer = function(reason = 'restarted', { liftPause = false } = {}) {
    const gate = this.displayGates.get(DisplayGateKeys.FIRST_SLOT_RESPONSE);
    if (!gate || gate.opened) return;

    const timeout = getFirstSlotResponseTimeout(this);
    const restarted = liftPause && gate.paused ? gate.resumeTimeout(timeout) : gate.restartTimeout(timeout);

    if (restarted) {
      Logger.log(`[DisplayGate] '${gate.key}' countdown restarted (${reason})`);
    }
  };
}

/**
 * Force-render the special slots whose IntersectionObserver never fires on its own — out-of-page
 * (a skin/wallpaper in a zero-visibility div) and adhesion (a sticky bar that starts collapsed).
 * They register via display() but only fetch through render(), which their observer never triggers,
 * so without this sweep they would stay registered but never requested.
 *
 * Driven by {@link ConcertAds#finalizeSlots} once the full slot list is known, before it releases the
 * first-slot gates — so these forced requests are ordered ahead of the pack the release unblocks,
 * and the adhesion slot can stay lazy (behind the gate) without an eager watcherEligible:false.
 * Skips eager slots (isEagerEligible()) and already-fetched ones (isDisplayed()).
 *
 * A forced request bypasses every gate, so a slot that waits on the still-closed `firstSlotResponse`
 * gate (by default, out-of-page and adhesion slots under `waitForFirstSlot`) is not forced now —
 * that would defeat the wait. Its forced request is instead deferred to the moment that gate opens
 * (first slot's response rendered, or its timeout), and is still forced then, so it keeps bypassing
 * every other gate. Those slots therefore no longer lead the pack while that gate is pending.
 *
 * @param {ConcertAds} app
 */
export function renderPendingSpecialSlots(app) {
  const responseGate = app.displayGates.get(DisplayGateKeys.FIRST_SLOT_RESPONSE);
  const responseGatePending = Boolean(responseGate) && !responseGate.opened;

  app.slots.all().forEach(slot => {
    // OOP and adhesion only; everything else fetches when it scrolls into view.
    if ((!slot.outOfPage && !slot.adhesion) || slot.isEagerEligible() || slot.isDisplayed()) return;

    const exempt = slot.exemptFromBlocking || [];
    if (responseGatePending && !exempt.includes(DisplayGateKeys.FIRST_SLOT_RESPONSE)) {
      // Repeat finalizeSlots() calls may register this twice; the second request is a no-op because
      // render() skips a slot that is already displayed. The destroyed / registration checks skip a
      // slot that was torn down while the gate was pending (a re-armed gate's old timer can still fire).
      responseGate.runOnOpen(() => {
        if (slot.isDisplayed() || slot.isDestroyed() || !app.slots.all().includes(slot)) return;
        slot.requestOrQueueForBid(true);
      });
      return;
    }

    // force=true: request now, ahead of the gate release — except a GAM slot mid-bid, which
    // requestOrQueueForBid defers to bidded() so it still renders with Prebid targeting.
    slot.requestOrQueueForBid(true);
  });
}
